﻿import Color from 'color';
import * as React from 'react';
import { LEGACY_FILL_ATTRIBUTES, LEGACY_STROKE_ATTRIBUTES, FILLABLE_ELEMENTS, STROKABLE_ELEMENTS } from '../../../../common/constants';
import { BaseResolver } from './base-resolver';

type ResolutionType = "process" | "registration";

interface ResolverState {
    selectedResolution: ResolutionType
}

// pd-x, pe-spot-x and pe-reg-x are legacy attributes.
const expectedFillAttribs = ["pe-fill", ...LEGACY_FILL_ATTRIBUTES];
const expectedStrokeAttribs = ["pe-stroke", ...LEGACY_STROKE_ATTRIBUTES];
const acceptableFillStrokeValues = ["none", "transparent"];

export class Resolver_Colours extends BaseResolver {
    public id: "resolver_colours";

    private state: ResolverState = {
        selectedResolution: "process",
    };

    private doesNeedResolution: boolean = false;

    public constructor(private svgRoot: SVGSVGElement) {
        super();

        this.doesNeedResolution = this.detectNeedsResolution(svgRoot);

        this.component = (
            <Resolver_Colours_Element selectedResolution={this.state.selectedResolution} onChange={v => this.onUpdate(v)} />
        );
    }

    public needResolution(): boolean {
        return this.doesNeedResolution;
    }

    public resolveSvg(svgContainer: HTMLTemplateElement) {
        this.resolveElement(svgContainer.firstElementChild as SVGSVGElement);
    }

    private onUpdate(newValues: ResolverState | { [key: string]: any }) {
        this.state = {
            ...this.state,
            ...newValues
        }
    }

    private detectNeedsResolution(el: SVGGraphicsElement): boolean {
        if (FILLABLE_ELEMENTS.includes(el.tagName.toLowerCase())) {
            const fill = (el.getAttribute("fill") ?? el.style.fill ?? "").trim().toLowerCase();

            if (!expectedFillAttribs.some(attrib => el.hasAttribute(attrib))
                && (fill.length == 0 || !acceptableFillStrokeValues.includes(fill))) {
                // Note: No fill is the same as fill="#000000" so that needs to be handled.
                console.log(el);
                return true;
            }
        }

        if (STROKABLE_ELEMENTS.includes(el.tagName.toLowerCase())) {
            const stroke = (el.getAttribute("stroke") ?? el.style.stroke ?? "").trim().toLowerCase();
            const strokeWidth = Number(el.getAttribute("stroke-width") ?? el.style.strokeWidth ?? 0);

            if (!expectedStrokeAttribs.some(attrib => el.hasAttribute(attrib))
                && (stroke.length > 0 && strokeWidth > 0 && !acceptableFillStrokeValues.includes(stroke))) {
                // Note: No fill is the same as stroke="#none" which doesn't need to be handled.
                //       This is why the second part of the if block above is inverted from the fill check.
                console.log(el);
                return true;
            }
        }

        let result: boolean = false;

        for (const child of el.children) {
            result = this.detectNeedsResolution(child as SVGGraphicsElement);
            if (result) {
                break;
            }
        }

        return result;
    }

    private resolveElement(el: SVGGraphicsElement) {
        // Note: Unlike detection, we don't need to handle el.style here. That's already been handled by Resolver_StyleToAttribute.

        if (FILLABLE_ELEMENTS.includes(el.tagName.toLowerCase())) {
            if (!el.hasAttribute("pe-fill")) {
                if (el.hasAttribute("pd-fill")) {
                    el.setAttribute("pe-fill", el.getAttribute("pd-fill"));
                } else if (el.hasAttribute("pe-reg-fill")) {
                    let regFill = el.getAttribute("pe-reg-fill");
                    if (regFill.trim().toLowerCase() === "none") {
                        regFill = "0";
                    }
                    el.setAttribute("pe-fill", `REGISTRATION|${regFill}`);
                } else {
                    let fill: Color | null = null;
                    try {
                        const fillAttr = (getAttributeFallback(el, "fill") ?? "#000000").trim().toLowerCase();
                        if (fillAttr != "none") {
                            fill = Color(fillAttr).cmyk();
                        }
                    } catch {
                        fill = null;
                    }

                    switch (this.state.selectedResolution) {
                        case "process":
                            el.setAttribute("pe-fill", fill ? `PROCESSCOL|_|${fill.cyan() / 100.0}|${fill.magenta() / 100.0}|${fill.yellow() / 100.0}|${fill.black() / 100.0}|1` : "NONE");
                            break;

                        case "registration":
                            el.setAttribute("pe-fill", fill ? `REGISTRATION|${(1.0 - fill.luminosity()) * 100.0}` : "NONE");
                            break;
                    }
                }

                el.removeAttribute("pd-fill");
                el.removeAttribute("pe-reg-fill");
                el.removeAttribute("pe-preview-fill");
            }
        }

        if (STROKABLE_ELEMENTS.includes(el.tagName.toLowerCase())) {
            if (!el.hasAttribute("pe-stroke")) {
                if (el.hasAttribute("pd-stroke")) {
                    el.setAttribute("pe-stroke", el.getAttribute("pd-stroke"));
                } else if (el.hasAttribute("pe-reg-stroke")) {
                    el.setAttribute("pe-stroke", `REGISTRATION|${el.getAttribute("pe-reg-stroke")}`);
                } else {
                    let stroke: Color | null = null;
                    try {
                        const strokeAttr = (getAttributeFallback(el, "stroke") ?? "none").trim().toLowerCase();
                        if (strokeAttr != "none") {
                            stroke = Color(strokeAttr).cmyk();
                        }
                    } catch {
                        stroke = null;
                    }

                    switch (this.state.selectedResolution) {
                        case "process":
                            el.setAttribute("pe-stroke", stroke ? `PROCESSCOL|_|${stroke.cyan() / 100.0}|${stroke.magenta() / 100.0}|${stroke.yellow() / 100.0}|${stroke.black() / 100.0}|1` : "NONE");
                            break;

                        case "registration":
                            el.setAttribute("pe-stroke", stroke ? `REGISTRATION|${(1.0 - stroke.luminosity()) * 100.0}` : "NONE");
                            break;
                    }
                }

                el.removeAttribute("pd-stroke");
                el.removeAttribute("pe-reg-stroke");
                el.removeAttribute("pe-preview-stroke");
            }
        }

        for (const child of el.children) {
            this.resolveElement(child as SVGGraphicsElement);
        }
    }
}

interface Props {
    selectedResolution: ResolutionType;
    onChange(newValues: { [key: string]: any }): void;
}

const Resolver_Colours_Element: React.FC<Props> = ({ selectedResolution, onChange }) => {
    const [resolution, setResolution] = React.useState(selectedResolution);

    const doSetSelectedResolution = (newValue: ResolutionType) => {
        onChange({ selectedResolution: newValue });
        setResolution(newValue);
    }

    return (
        <fieldset>
            <legend>Colours</legend>

            <p>It was detected that this panel has elements that contain non-AutoArt fill or stroke colours (ie, No CMYK, Registration or Spot Colour defined).</p>
            <p>The SVG import can attempt to resolve the existing RGB colours in one of the following ways:</p>

            <div className="row g-2 align-items-center">
                <div className="col-auto">
                    <input type="radio" id="coloursresolve_processcolours" name="coloursresolve_resolvetype" className="form-check-input" aria-describedby="coloursresolve_processcolours__help"
                        value="process" checked={resolution === "process"} onChange={e => doSetSelectedResolution(e.target.value as ResolutionType)} />
                    <label htmlFor="coloursresolve_processcolours" className="form-check-label">Convert to CMYK Process colours</label>
                </div>
                <div className="col-auto">
                    <span id="coloursresolve_processcolours__help" className="form-text">
                        The RGB values will be converted to CMYK process colours as closely as possible using the sRGB colour space.
                    </span>
                </div>
            </div>

            <div className="row g-2 align-items-center">
                <div className="col-auto">
                    <input type="radio" id="coloursresolve_registration" name="coloursresolve_resolvetype" className="form-check-input" aria-describedby="coloursresolve_registration__help"
                        value="registration" checked={resolution === "registration"} onChange={e => doSetSelectedResolution(e.target.value as ResolutionType)} />
                    <label htmlFor="coloursresolve_registration" className="form-check-label">Convert to Registration</label>
                </div>
                <div className="col-auto">
                    <span id="coloursresolve_registration__help" className="form-text">
                        The RGB values will be converted to registration based on their luminance values. Use this setting if you're importing a panel/legend.
                    </span>
                </div>
            </div>
        </fieldset>
    );
}

function getAttributeFallback(element: SVGGraphicsElement, attributeToGet: string): string | null {
    if (!element) {
        return null;
    }

    return element.getAttribute(attributeToGet)
        ?? getAttributeFallback(element.parentElement as unknown as SVGGraphicsElement, attributeToGet);
}
